import Cookies from 'js-cookie'
import { isPlainObject, isNaN } from 'lodash'
import { UserSeparator } from './stringUtil'
import { t } from '@/plugins/i18n'

export const CURRENCY_MAP = {
  AUD: 'AUD',
  BRL: 'BRL',
  CAD: 'CAD',
  CHF: 'CHF',
  CNY: 'CNY',
  DKK: 'DKK',
  EUR: 'EUR',
  GBP: 'GBP',
  INR: 'INR',
  IDR: 'IDR',
  JPY: 'JPY',
  KRW: 'KRW',
  MXN: 'MXN',
  MYR: 'MYR',
  NZD: 'NZD',
  PHP: 'PHP',
  SEK: 'SEK',
  SGD: 'SGD',
  TWD: 'TWD',
  USD: 'USD',
  VND: 'VND',
  HKD: 'HKD',
  PLN: 'PLN',
  NOK: 'NOK',
  SAR: 'SAR',
  ZAR: 'ZAR',
  AED: 'AED'
}

const DEFAULT_CURRENCY = CURRENCY_MAP.USD

/**
 * This function contains code that was transferred from the "fe-billing-web//src/const"
 */
export const NO_DECIMAL_CURRENCIES = [CURRENCY_MAP.VND, CURRENCY_MAP.KRW, CURRENCY_MAP.IDR, CURRENCY_MAP.JPY, CURRENCY_MAP.INR]

const CURRENCY_SYMBOL_MAP = {
  [CURRENCY_MAP.AUD]: 'A$',
  [CURRENCY_MAP.BRL]: 'R$',
  [CURRENCY_MAP.CAD]: 'CAD',
  [CURRENCY_MAP.CHF]: 'CHF',
  [CURRENCY_MAP.CNY]: '\uFFE5',
  [CURRENCY_MAP.DKK]: '\u006b\u0072',
  [CURRENCY_MAP.EUR]: '\u20AC',
  [CURRENCY_MAP.GBP]: '\u00A3',
  [CURRENCY_MAP.INR]: '\u20b9',
  [CURRENCY_MAP.IDR]: 'Rp',
  [CURRENCY_MAP.JPY]: '\uFFE5',
  [CURRENCY_MAP.KRW]: '\u20a9',
  [CURRENCY_MAP.MXN]: 'MX$',
  [CURRENCY_MAP.MYR]: 'RM',
  [CURRENCY_MAP.NZD]: 'NZ$',
  [CURRENCY_MAP.PHP]: '\u20b1',
  [CURRENCY_MAP.SEK]: 'kr',
  [CURRENCY_MAP.SGD]: 'S$',
  [CURRENCY_MAP.TWD]: 'NT$',
  [CURRENCY_MAP.USD]: '$',
  [CURRENCY_MAP.VND]: '\u20ab',
  [CURRENCY_MAP.HKD]: 'HK$',
  [CURRENCY_MAP.PLN]: 'z\u0142',
  [CURRENCY_MAP.NOK]: 'kr',
  [CURRENCY_MAP.SAR]: 'SAR',
  [CURRENCY_MAP.ZAR]: 'R',
  [CURRENCY_MAP.AED]: 'AED'
}
export const BIZ_PLUS_UNAVAILABLE_CURRENCY = ['INR', 'VND', 'BRL', 'MXN', 'IDR', 'PHP', 'KRW', 'MYR']
export const PRO_PLUS_UNAVAILABLE_CURRENCY = ['INR', 'VND', 'BRL', 'MXN', 'IDR', 'PHP', 'KRW', 'MYR']
export const ZP_UNAVAILABLE_CURRENCY = ['INR', 'VND', 'BRL', 'MXN', 'IDR', 'PHP', 'KRW', 'MYR']

/**
 * A function that checks if the currency obtained from getStoreCurrency is in the ZP_UNAVAILABLE_CURRENCY array.
 *
 * @return {boolean} true if the currency is in the ZP_UNAVAILABLE_CURRENCY array, false otherwise
 */
export const isZpUnavailableCurrency = () => {
  const currency = getStoreCurrency()
  return ZP_UNAVAILABLE_CURRENCY.includes(currency)
}

/**
 * This function contains code that was transferred from the "fe-billing-web/src/common/util"
 * @param {string} currency - Currency code
 * @description having a currency code returns the currency symbol
 * @returns {string} currency symbol
 */
export const getCurrencySymbol = (currency) => {
  return CURRENCY_SYMBOL_MAP[currency] || currency
}

export const addThousandSeparator = (num, comma = UserSeparator['commaStyle']) => {
    return (num || 0).toString().replace(/(\d)(?=(?:\d{3})+$)/g, `$1${comma}`)
  }

/**
 * @param {Object} formatOptions Currency format options
 * @param {number} formatOptions.amountInCents Amount in cents
 * @param {number|Object} formatOptions.fixedLengthOrOptions Number: 2, Object: { fixedLength:2, divisionNumber: 1, forceDiff: false, customizedCurrencySymbol: '$'}
 * @param {string} formatOptions.customizedCurrencySymbol custom currency symbol
 * @param {boolean} formatOptions.shouldHideTrailingZeros If set to true then it would hide the trailing zeros from amount
 * @description Transfer amount in cents to a standard format with configuration
 * @returns {string} Amount with standard format
 */
export function formatCurrency({
  amountInCents = 0,
  fixedLengthOrOptions,
  customizedCurrencySymbol,
  shouldHideTrailingZeros = false
}) {
  if (amountInCents === undefined) return;
  let currencyFixedLength = 2
  const { commaStyle, decimal } = UserSeparator
  if (isNaN(+amountInCents)) return amountInCents.toString()

  const isNoDecimalCurrencies = NO_DECIMAL_CURRENCIES.indexOf(getStoreCurrency()) !== -1

  // Mainly to be compatible with the old way of passing parameters
  if (isPlainObject(fixedLengthOrOptions)) {
    currencyFixedLength = isNoDecimalCurrencies ? 0 : getDecimalsWithCurrency(amountInCents, fixedLengthOrOptions)
  } else {
    currencyFixedLength = isNoDecimalCurrencies ? 0 : fixedLengthOrOptions ?? 2
  }

  const fixedDollars = (amountInCents / 100).toFixed(currencyFixedLength)

  let formatVal
  if (shouldHideTrailingZeros) {
    formatVal = addThousandSeparator(parseFloat(fixedDollars), commaStyle)
  } else {
    const moneySplit = fixedDollars.split('.')
    formatVal = `${addThousandSeparator(moneySplit[0], commaStyle)}${moneySplit[1] ? `${decimal}${moneySplit[1]}` : ''}`
  }
  return addSymbol(formatVal, customizedCurrencySymbol)
}

/**
 * This function contains code that was transferred from the "fe-billing-web/src/common/util"
 * @param {number} formatVal - Amount
 * @param {string} customizedCurrencySymbol - Currency symbol
 * @returns {string}
 */
export const addSymbol = (formatVal, customizedCurrencySymbol) => {
  const { place, space, specialSymbol } = UserSeparator
  const currencySymbol = customizedCurrencySymbol || getCurrencySymbol(getStoreCurrency())
  const currencyTrans = 'currency.' + getStoreCurrency().toLowerCase()
  let getCurrencyTrans
  if (customizedCurrencySymbol) {
    getCurrencyTrans = currencySymbol
  } else if (t(currencyTrans) == currencyTrans) {
    console.error('Cannot get translation of this currency.')
    getCurrencyTrans = currencySymbol
  } else {
    getCurrencyTrans = t(currencyTrans)
  }
  const formatSymbol = (specialSymbol && getCurrencyTrans) || currencySymbol
  return place === 'begin'
    ? `${formatSymbol}${space ? ' ' : ''}${formatVal}`
    : `${formatVal}${space ? ' ' : ''}${formatSymbol}`
}

/**
 * This function contains code that was transferred from the "fe-billing-web/src/common/util"
 * @param {number} amount
 * @param {{divisionNumber: number, forceDiff: boolean, fixedLength: number, withCurrency: boolean}} options
 * @returns {string}
 */
export const getDecimalsWithCurrency = (
  amount,
  options = {
    divisionNumber: 1,
    forceDiff: false, // eg: the value is true means $200.00 -> $200
    fixedLength: 2, // decimal length
    withCurrency: true // It is necessary to judge whether decimal places are required according to different currencies
  }
) => {
  if (isNaN(+amount)) return 0

  const { forceDiff = false, divisionNumber = 1, fixedLength = 2, withCurrency = true } = options

  if (!withCurrency) return fixedLength

  const currency = getStoreCurrency()
  const formatIntegerCurrencyList = [CURRENCY_MAP.JPY, CURRENCY_MAP.INR, CURRENCY_MAP.VND]

  if (formatIntegerCurrencyList.includes(currency)) {
    return 0
  } else {
    if (forceDiff) {
      return parseInt((amount / divisionNumber).toString()) === parseFloat((amount / divisionNumber).toString()) ? 0 : 2
    } else {
      return fixedLength
    }
  }
}


export const CURRENCY_COOKIE_KEY = '_zm_currency'
/**
 * This function contains code that was transferred from the "fe-billing-web/src/common/util"
 * @returns {string} Currency code
 */
let CURRENCY = ''
export const getStoreCurrency = () => {
  if (CURRENCY) {
    return CURRENCY
  } else {
    return Cookies.get(CURRENCY_COOKIE_KEY) || DEFAULT_CURRENCY
  }
}

// Returns true if the given amount is 0, ignoring the decimals after the user's currency max decimals.
// i.e. for 0.0003 it would return true for USD.
export const roundedAmountIsZero = (
  amount,
  options = {
    divisionNumber: 100
  }
) => {
  if (isNaN(+amount)) return false
  const zeroVal = 0
  const { divisionNumber } = options
  const currencyFixedLength = getDecimalsWithCurrency(amount)
  return (amount / divisionNumber).toFixed(currencyFixedLength) === zeroVal.toFixed(currencyFixedLength)
}
